import React, { useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { setSiteAccessVerified } from '../../../app/routes/RouteGuards';

const SITE_ACCESS_USERNAME = process.env.REACT_APP_SITE_ACCESS_USERNAME || '';
const SITE_ACCESS_PASSWORD = process.env.REACT_APP_SITE_ACCESS_PASSWORD || '';

const isConfigured = () => {
  return Boolean(SITE_ACCESS_USERNAME && SITE_ACCESS_PASSWORD);
};

const getSafeNextPath = (locationSearch) => {
  const params = new URLSearchParams(locationSearch);
  const next = params.get('next') || '/';

  if (!next.startsWith('/') || next.startsWith('/site-access')) {
    return '/';
  }

  return next;
};

const SiteAccessVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const alreadyVerified = localStorage.getItem('siteAccessVerified') === 'true';

  const nextPath = useMemo(() => getSafeNextPath(location.search), [location.search]);

  if (alreadyVerified) {
    return <Navigate to={nextPath} replace />;
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    setError('');

    if (!isConfigured()) {
      setError('Site access credentials are not configured. Please contact the administrator.');
      return;
    }

    const normalizedUsername = username.trim();

    if (
      normalizedUsername === SITE_ACCESS_USERNAME.trim() &&
      password === SITE_ACCESS_PASSWORD
    ) {
      setSiteAccessVerified();
      navigate(nextPath, { replace: true });
      return;
    }

    setError('Invalid access credentials.');
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl p-8 border border-slate-200">
        <h1 className="text-2xl font-bold text-slate-900">Restricted Access</h1>
        <p className="mt-2 text-sm text-slate-600">
          This environment is private. Enter your assigned credentials to continue.
        </p>

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="site-username" className="block text-sm font-medium text-slate-700">
              Username
            </label>
            <input
              id="site-username"
              type="text"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
              autoComplete="username"
              required
            />
          </div>

          <div>
            <label htmlFor="site-password" className="block text-sm font-medium text-slate-700">
              Password
            </label>
            <input
              id="site-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
              autoComplete="current-password"
              required
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button
            type="submit"
            className="w-full rounded-md bg-slate-900 text-white py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            Verify and Continue
          </button>
        </form>
      </div>
    </div>
  );
};

export default SiteAccessVerification;
